// src/components/Assets/AssetRequests/index.js
// View for managing asset permission requests (both farmers and admins can request)

import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { UserContext } from "../../../UserContext";
import useAssetPermissions from "../hooks/useAssetPermissions";
import useAssets from "../hooks/useAssets";
import RequestAssetForm from "./RequestAssetForm";
import PermissionCard from "../AssetPermissions/PermissionCard";
import "../style.css";
import "../AssetPermissions/PermissionStyles.css";

const AssetRequests = () => {
  const [t] = useTranslation();
  const { userData } = useContext(UserContext);

  // Determine if user has a farmId (farmers will have one, admins might not)
  const farmId = userData?.Employee?.farmId;

  // Use "farmer" view if user has farmId, otherwise use "admin" but filter to show only user's requests
  const { permissions, loading, requestPermission } = useAssetPermissions(farmId ? "farmer" : "admin");
  const { assets, loading: assetsLoading, fetchAssets } = useAssets();
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Fetch available assets
    fetchAssets();
  }, [fetchAssets]);

  const handleSubmitRequest = async (requestData) => {
    setIsSubmitting(true);
    const success = await requestPermission(requestData);
    setIsSubmitting(false);

    if (success) {
      setShowForm(false);
    }
  };

  // Debug: Log permissions in AssetRequests component
  useEffect(() => {
    console.log("AssetRequests - permissions array:", permissions);
    console.log("AssetRequests - permissions.length:", permissions.length);
  }, [permissions]);

  if (loading || assetsLoading) {
    return (
      <div className="assets-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-container">
      {/* Header */}
      <div className="assets-header">
        <div>
          <h2>{t("My Asset Requests")}</h2>
          <p className="subtitle">{t("View and manage your asset permission requests")}</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          <FaPlus /> {t("New Request")}
        </button>
      </div>

      {/* Requests List */}
      {permissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>{t("No Requests Yet")}</h3>
          <p>{t("Click 'New Request' to request permission to use an asset")}</p>
        </div>
      ) : (
        <div className="permissions-list">
          {permissions.map(permission => (
            <PermissionCard
              key={permission.id}
              permission={permission}
              showActions={false}
            />
          ))}
        </div>
      )}

      {/* Request Form Modal */}
      <RequestAssetForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitRequest}
        isSubmitting={isSubmitting}
        assets={assets}
      />
    </div>
  );
};

export default AssetRequests;
